import { NumberTrace } from './number-provenance';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { ChatCircleDots, X } from '@phosphor-icons/react';
import { trackFunnel } from './analytics';
import { explainerAnalytics, type NumbersExplainer } from './numbers-explainer-model';

export function NumbersExplainerSheet({ explainer, failed, onRetry, onClose, onAsk }: {
  explainer: NumbersExplainer | null;
  failed: boolean;
  onRetry: () => void;
  onClose: () => void;
  onAsk: (bridge: NumbersExplainer['bridge']) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const seen = useRef(new Set<string>());

  useEffect(() => { closeRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // One impression per number and one per bridge for each open of the sheet.
  useEffect(() => {
    const root = scrollRef.current;
    if (!explainer || !root) return;
    const fields = explainerAnalytics(explainer.bridge);
    const once = (key: string, fire: () => void) => {
      if (seen.current.has(key)) return;
      seen.current.add(key);
      fire();
    };
    const bridgeSeen = () => once('bridge', () => void trackFunnel('numbers_explainer_primary_question_impression', fields));
    if (typeof IntersectionObserver === 'undefined') { bridgeSeen(); return; }
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const numberType = (entry.target as HTMLElement).dataset.numberType;
        if (numberType) once(`item:${numberType}`, () => void trackFunnel('numbers_explainer_item_viewed', { sourceSurface: fields.sourceSurface, numberType }));
        else bridgeSeen();
      }
    }, { root, threshold: 0.6 });
    root.querySelectorAll('[data-number-type], [data-explainer-bridge]').forEach(target => observer.observe(target));
    return () => observer.disconnect();
  }, [explainer]);

  return createPortal(<div className="modal-backdrop numbers-explainer-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <motion.div
      className="bottom-sheet numbers-explainer-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="numbers-explainer-title"
      aria-describedby="numbers-explainer-subtitle"
      data-testid="numbers-explainer"
      initial={{ y: 60 }}
      animate={{ y: 0 }}
    >
      <button ref={closeRef} type="button" className="icon-button sheet-close" onClick={onClose} aria-label="Закрыть пояснение"><X /></button>
      <div className="numbers-explainer-scroll" ref={scrollRef}>
        <p className="eyebrow">Твоя карта</p>
        <h2 id="numbers-explainer-title">{explainer?.title ?? 'Что означают твои числа?'}</h2>
        <p id="numbers-explainer-subtitle" className="numbers-explainer-subtitle">{explainer?.subtitle ?? 'Коротко покажу, за что отвечает каждый показатель в твоей карте.'}</p>
        {!explainer && (failed
          ? <div className="numbers-explainer-state" role="alert"><p>Не удалось загрузить пояснение. Числа в карте не изменились.</p><button type="button" className="secondary-button" onClick={onRetry}>Повторить</button></div>
          : <div className="numbers-explainer-state" aria-live="polite"><p>Сверяю пояснение с твоей картой...</p></div>)}
        {explainer && <>
          <ul className="numbers-explainer-list">
            {explainer.items.map(item => <li key={item.numberType} className="numbers-explainer-card" data-number-type={item.numberType} data-system={item.system}>
              <div className="nx-head">
                <div><small className="nx-system">{item.systemLabel}</small><h3>{item.label}</h3></div>
                <span className="nx-value" aria-label={`Значение ${item.value}`}>{item.value}</span>
              </div>
              {item.calculationTrace && <NumberTrace trace={item.calculationTrace} />}
              <p className="nx-role"><strong>За что отвечает:</strong> {item.role}</p>
              {item.meaning ? <p className="nx-meaning">{item.meaning}</p> : item.note && <p className="nx-note">{item.note}</p>}
            </li>)}
          </ul>
          <section className="numbers-explainer-bridge" data-explainer-bridge="primary" aria-labelledby="numbers-explainer-bridge-title">
            <h3 id="numbers-explainer-bridge-title">{explainer.bridge.heading}</h3>
            <p className="nx-question">{explainer.bridge.question}</p>
            <button type="button" className="primary-button nx-ask" data-testid="numbers-explainer-ask" onClick={() => onAsk(explainer.bridge)}>
              <ChatCircleDots weight="fill" />{explainer.bridge.cta}
            </button>
          </section>
        </>}
      </div>
    </motion.div>
  </div>, document.body);
}
